import React, { useState, useRef, useEffect } from "react";
import { apiService } from "../../../services/apiService";
import "./AddEmulator.css";
import { Button } from "../../components/Utilities";
import { Loading } from "../../components/Loading"; // ✅ Import your Loading component
import pro from "../../assets/person.svg";
import camera from "../../assets/photo.svg";
import share from "../../assets/share.svg";

export const AddEvaluatorModal = ({ isOpen, onClose, onDriverAdded }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  const [licensePreview, setLicensePreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // ✅ Controls Loading overlay
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const profileInputRef = useRef(null);
  const licenseInputRef = useRef(null);

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((err) => {
        console.error("Video play blocked:", err);
      });
    }
  }, [showCamera, stream]);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!(file instanceof File)) {
        console.error("fileToBase64: Invalid file input:", file);
        reject(new Error("Invalid file input: File object is required"));
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result?.split(",")[1];
        if (!result) reject(new Error("Failed to convert file to base64"));
        else resolve(result);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  if (!isOpen) return null;

  const handleLicenseUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
      if (!validTypes.includes(file.type)) {
        setError("Only JPEG, PNG, JPG, or PDF files are allowed for license.");
        return;
      }
      setLicenseFile(file);
      setLicensePreview(["image/jpeg", "image/png", "image/jpg"].includes(file.type) ? URL.createObjectURL(file) : null);
      setError(null);
    }
  };

  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        setError("Only JPEG, PNG, or JPG files are allowed for profile picture.");
        return;
      }
      setProfileFile(file);
      setProfilePicture(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleAddEvaluator = async () => {
    if (!name || !phone || !licenseFile || !profileFile) {
      setError("Please fill in all required fields and upload both license and profile picture.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload license
      const licenseBase64 = await fileToBase64(licenseFile);
      const licenseExtension = licenseFile.type === "application/pdf" 
        ? "pdf" 
        : licenseFile.type === "image/jpeg" || licenseFile.type === "image/jpg" 
        ? "jpg" : "png";
      const licenseFileName = `evaluator_license_${name}_${Date.now()}.${licenseExtension}`;
      const licenseUploadData = {
        file: licenseBase64,
        folder: "drivers",
        fileName: licenseFileName,
        metadata: {
          contentType: licenseFile.type,
          originalName: licenseFile.name,
          size: licenseFile.size,
          documentType: "license",
        },
      };
      console.log("Uploading license file:", { fileName: licenseFileName, size: licenseFile.size });
      const licenseResponse = await apiService.uploadFile(licenseUploadData);
      console.log("License upload response:", licenseResponse.data);
      const licenseUrl = licenseResponse.data?.publicUrl || "";

      // Upload profile
      const profileBase64 = await fileToBase64(profileFile);
      const profileExtension = profileFile.type === "image/jpeg" || profileFile.type === "image/jpg" 
        ? "jpg" : "png";
      const profileFileName = `evaluator_profile_${name}_${Date.now()}.${profileExtension}`;
      const profileUploadData = {
        file: profileBase64,
        folder: "drivers",
        fileName: profileFileName,
        metadata: {
          contentType: profileFile.type,
          originalName: profileFile.name,
          size: profileFile.size,
          documentType: "profile",
        },
      };
      console.log("Uploading profile picture:", { fileName: profileFileName, size: profileFile.size });
      const profileResponse = await apiService.uploadFile(profileUploadData);
      console.log("Profile upload response:", profileResponse.data);
      const imageUrl = profileResponse.data?.publicUrl || "";

      // Create evaluator
      const driverData = {
        name,
        phone,
        licenseUrl,
        imageUrl,
        type: "evaluator",
      };

      await apiService.createDriver(driverData);
      onDriverAdded();

      // Reset form
      setName("");
      setPhone("");
      setLicenseFile(null);
      setLicensePreview(null);
      setProfileFile(null);
      setProfilePicture(null);
      onClose();
    } catch (err) {
      console.error("Error creating evaluator:", err);
      let errorMessage = "Failed to add evaluator. Please try again.";
      if (err.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please check your network or try again later.";
      } else if (err.response?.status === 400) {
        errorMessage = `Invalid data provided: ${err.response.data?.error || "Please check your inputs."}`;
      } else if (err.response?.status === 401) {
        errorMessage = "Unauthorized. Please log in again.";
      } else if (err.response?.status === 500) {
        errorMessage = `Server error: ${err.response.data?.error || "Please try again later."}`;
      } else {
        errorMessage = `Failed to add evaluator: ${err.message || "Unknown error"}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCamera = async () => {
    setLoading(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
      });
      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Unable to access camera. Please allow permissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const file = new File([blob], "captured-photo.jpg", { type: "image/jpeg" });
      setProfileFile(file);
      setProfilePicture(URL.createObjectURL(file));
    });

    stopCamera();
  };

  const stopCamera = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setShowCamera(false);
    setStream(null);
  };

  const handleImageClick = (img) => {
    setSelectedImage(img);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <div className="add-emulator">
      <div className="modal-overlay">
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="model-header">
            <p>Add an Evaluator</p>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>

          <div className="model-body">
 

            {showCamera && (
              <div className="camera-overlay">
                <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
                <canvas ref={canvasRef} style={{ display: "none" }} />
                <div className="camera-controls">
                  <Button className="add-evaluator-btn" onClick={handleCapture}>
                    Capture
                  </Button>
                  <Button className="upload-btn-modal" onClick={stopCamera}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="right-part">
              <div className="evaluator-photo">
                <div className="profile-pic">
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt="Profile"
                      className="uploadModel-img"
                      onClick={() => handleImageClick(profilePicture)}
                    />
                  ) : (
                    <img src={pro} alt="Default" width="50%" />
                  )}
                </div>

                <div className="evaluator-section-part">
                  {/* <Button className="model-btn" onClick={handleOpenCamera}>
                    <img src={camera} alt="" className="camera" /> Take Photo
                  </Button> */}

                  <input
                    type="file"
                    accept="image/heic,image/heif,image/jpeg,image/jpg,image/png,image/gif,image/tiff,image/webp,image/bmp,image/x-adobe-dng,image/x-dng,application/pdf,video/mp4,video/quicktime,video/x-matroska,video/webm,video/3gpp,video/x-msvideo,video/hevc,video/h265"

                      //  accept="image/jpeg,image/jpg,image/png,application/pdf"
                    ref={profileInputRef}
                    onChange={handleProfilePictureUpload}
                    style={{ display: "none" }}
                  />

                  <Button
                    className="model-btn"
                    onClick={() => {
                      setLoading(true);
                      if (profileInputRef.current) {
                        profileInputRef.current.value = "";
                        profileInputRef.current.click();
                      }
                      setTimeout(() => setLoading(false), 600);
                    }}
                  >
                    <img src={share} className="share" alt="" /> Upload Photo
                  </Button>
                </div>
              </div>

              <div className="emulator-inputs">
                <div className="eva-name">
                <label>Evaluator’s Name*</label>
                <input
                  type="text"
                  value={name}
                   onChange={(e) => {
                      const value = e.target.value;
                      // Allow only letters (a-z, A-Z) and spaces; limit to 50 characters
                      if (/^[a-zA-Z\s]*$/.test(value) && value.length <= 50) {
                        setName(value);
                      }
                    }}
                    pattern="[a-zA-Z\s]{2,50}"
                    title="Name must contain only letters and spaces, and be between 2 and 50 characters."
                    placeholder="Enter your name"
                    required
                /></div>
                <div>
                <label>Evaluator’s Phone Number*</label>
                <input
                  type="tel"
                  value={phone}
                   onChange={(e) => {
                      const value = e.target.value;
                      // Allow only digits and limit to 10 characters
                      if (/^\d*$/.test(value) && value.length <= 10) {
                        setPhone(value);
                      }
                    }}
                    pattern="[0-9]{10}"
                    placeholder="Enter your Phone Number"
                    required
                />
              </div></div>
            </div>

            <div className="left-part">
              <div className="license-upload-part">
                <div className="license-preview">
                  {licensePreview ? (
                    <img
                      src={licensePreview}
                      alt="License"
                      className="uploadModel-img"
                      onClick={() => handleImageClick(licensePreview)}
                    />
                  ) : (
                    <img src={pro} alt="Default" width="50%" />
                  )}
                </div>

                <input
                  type="file"
                  accept="image/heic,image/heif,image/jpeg,image/jpg,image/png,image/gif,image/tiff,image/webp,image/bmp,image/x-adobe-dng,image/x-dng,application/pdf,video/mp4,video/quicktime,video/x-matroska,video/webm,video/3gpp,video/x-msvideo,video/hevc,video/h265"

                    //  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  ref={licenseInputRef}
                  onChange={handleLicenseUpload}
                  style={{ display: "none" }}
                />

                <Button
                  className="upload-btn-modal"
                  onClick={() => {
                    setLoading(true);
                    if (licenseInputRef.current) {
                      licenseInputRef.current.value = "";
                      licenseInputRef.current.click();
                    }
                    setTimeout(() => setLoading(false), 600);
                  }}
                >
                  <img src={share} className="share" alt="" /> Upload Evaluator’s License*
                </Button>
                           {error && <p className="error">{error}</p>}
              </div>

              <Button
                className="add-evaluator-btn"
                onClick={handleAddEvaluator}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Evaluator"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Image preview modal */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={handleCloseImageModal}>
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full-size Preview"
              className="full-size-image"
            />
          </div>
        </div>
      )}

      {/* ✅ Global Loading Overlay */}
      {loading && <Loading />}
    </div>
  );
};
